import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { AuthContext, type AuthContextType } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

// Global state to track if user was banned and needs blocking overlay
let isBlocked = false;
let blockingOverlayElement: HTMLElement | null = null;

const createBlockingOverlay = () => {
  if (blockingOverlayElement) return blockingOverlayElement;

  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(2px);
  `;

  const message = document.createElement('div');
  message.style.cssText = `
    background: white;
    padding: 2rem;
    border-radius: 0.5rem;
    text-align: center;
    max-width: 400px;
    margin: 1rem;
  `;
  message.innerHTML = `
    <h3 style="margin-bottom: 1rem; color: #ef4444;">Sesión Cerrada</h3>
    <p style="color: #374151;">Tu sesión fue cerrada por un administrador.</p>
  `;

  overlay.appendChild(message);
  blockingOverlayElement = overlay;
  return overlay;
};

export const useBanStatus = () => {
  const authContext = useContext(AuthContext);

  // Gracefully handle case where hook is called outside of AuthProvider
  if (!authContext) {
    return { isBanned: false, loading: true };
  }

  const { user, signOut } = authContext;
  const [isBanned, setIsBanned] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastCheckRef = useRef<number>(0);
  const isCheckingRef = useRef(false);

  const handleBanDetected = useCallback(async () => {
    isBlocked = true;
    setIsBanned(true);

    // Add blocking overlay to DOM
    const overlay = createBlockingOverlay();
    document.body.appendChild(overlay);

    toast({
      title: "Sesión cerrada",
      description: "Tu sesión fue cerrada por un administrador.",
      variant: "destructive"
    });

    setTimeout(() => {
      signOut();
    }, 2000);
  }, [signOut]);

  const checkBanStatus = useCallback(async (userId: string, immediate = false) => {
    // Prevent concurrent checks and throttle requests (unless immediate)
    if (isCheckingRef.current) return;

    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckRef.current;

    // Throttle checks to maximum once every 45 seconds (unless immediate)
    if (!immediate && timeSinceLastCheck < 45000) return;

    isCheckingRef.current = true;
    lastCheckRef.current = now;

    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('is_banned')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking ban status:', error);
        setIsBanned(false);
        return;
      }

      if (data?.is_banned) {
        await handleBanDetected();
      } else {
        setIsBanned(data?.is_banned || false);
      }
    } catch (error) {
      console.error('Error checking ban status:', error);
      setIsBanned(false);
    } finally {
      isCheckingRef.current = false;
      setLoading(false);
    }
  }, [handleBanDetected]);

  // Check if user navigates while banned - auto logout
  useEffect(() => {
    if (isBlocked && user) {
      // If user is blocked and navigates, sign them out immediately
      const handleLocationChange = () => {
        if (isBlocked) {
          signOut();
        }
      };

      // Listen for navigation changes
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;

      window.history.pushState = function (...args) {
        handleLocationChange();
        return originalPushState.apply(window.history, args);
      };

      window.history.replaceState = function (...args) {
        handleLocationChange();
        return originalReplaceState.apply(window.history, args);
      };

      window.addEventListener('popstate', handleLocationChange);

      return () => {
        window.history.pushState = originalPushState;
        window.history.replaceState = originalReplaceState;
        window.removeEventListener('popstate', handleLocationChange);
      };
    }
  }, [user, signOut]);

  useEffect(() => {
    if (!user?.id) {
      setIsBanned(false);
      setLoading(false);
      return;
    }

    // Initial check (immediate)
    checkBanStatus(user.id, true);

    // Check every 2 minutes (less frequent to prevent spam)
    const interval = setInterval(() => {
      checkBanStatus(user.id);
    }, 120000);

    return () => clearInterval(interval);
  }, [user?.id, checkBanStatus]);

  return { isBanned, loading };
};